import React, { useEffect, useState } from 'react';
import { browseServerFs, FsEntry } from '../../../../utils/dataDefinitionService';
import { useTranslation } from 'react-i18next';

interface Props {
  onSelect: (relPath: string) => void;
  onClose: () => void;
}

/**
 * SAM파일 정의 시 "폴더의 파일 선택"을 위한 서버 파일시스템 브라우저.
 * 백엔드 SAM_BROWSE_ROOT 하위만 조회 가능 (경로 이탈 차단됨).
 */
const ServerFileBrowserDialog: React.FC<Props> = ({ onSelect, onClose }) => {
  const { t } = useTranslation();
  const [path, setPath] = useState('');
  const [parent, setParent] = useState<string | null>(null);
  const [entries, setEntries] = useState<FsEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = (p: string) => {
    setLoading(true);
    setError(null);
    browseServerFs(p)
      .then((res) => {
        setPath(res.path);
        setParent(res.parent);
        setEntries(res.entries);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded shadow-lg w-[520px] max-w-[90vw] max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">{t('datadef:browser.title', { path: path || '' })}</h3>
          <button className="text-gray-400 hover:text-gray-600" onClick={onClose}>✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading && <div className="text-xs text-gray-400 p-2">{t('common:ui.loading')}</div>}
          {error && <div className="text-xs text-red-500 p-2">{error}</div>}
          {!loading && !error && (
            <ul className="text-sm">
              {parent !== null && (
                <li
                  className="px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer text-gray-500"
                  onClick={() => load(parent)}
                >
                  {t('datadef:browser.parent')}
                </li>
              )}
              {entries.map((entry) => (
                <li
                  key={entry.path}
                  className="px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                  onClick={() => (entry.type === 'dir' ? load(entry.path) : onSelect(entry.path))}
                >
                  <span>{entry.type === 'dir' ? '📁' : '📄'}</span>
                  <span className="flex-1 truncate">{entry.name}</span>
                  {entry.type === 'file' && entry.size !== null && (
                    <span className="text-[0.6875rem] text-gray-400">{entry.size}B</span>
                  )}
                </li>
              ))}
              {entries.length === 0 && (
                <li className="px-2 py-1.5 text-xs text-gray-400">{t('datadef:browser.empty')}</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServerFileBrowserDialog;
